import React from "react";
import _ from "lodash";
import { sendDSUsageMetrics } from "./MetricsServerHelpers";
import { recordDesignSystemUsageMetrics } from "config";

/**
 * @typedef {Object} MetricData
 * @property {string} componentName - Name of the DS component
 * @property {string} [location] - Location where component is used
 * @property {string} [orgId] - Organization ID
 * @property {Object<string, *>} props - Component props to track
 */

/**
 * @typedef {Object} RegistryEntry
 * @property {MetricData} metadata - Original metric data
 * @property {number} count - Number of times this component was registered
 */

/**
 * Manager class for tracking Design System component usage analytics
 * Maintains a registry of component usage with counts
 */

class DSComponentMetricsManager {
  constructor() {
    if (recordDesignSystemUsageMetrics) {
      this.worker = new Worker(
        new URL("./WebWorkers/metricsHelper.worker.js", import.meta.url)
      );

      this.worker.onmessage = event => {
        if (event.data.type === "FLUSH") {
          if (_.isEmpty(event.data.payload)) {
            return;
          }
          sendDSUsageMetrics({ metrics: event.data.payload });
        }
      };
    } else {
      this.worker = null;
    }
  }

  sanitizeValue(value, seen = new WeakSet()) {
    if (_.isNil(value)) {
      return value;
    }

    if (_.isFunction(value)) {
      return "[function]";
    }

    if (_.isSymbol(value)) {
      return String(value);
    }

    if (_.isElement(value) || React.isValidElement(value)) {
      return "[element]";
    }

    if (_.isDate(value)) {
      return value.toISOString();
    }

    if (!_.isObject(value)) {
      return value;
    }

    if (seen.has(value)) {
      return "[circular]";
    }

    seen.add(value);

    if (_.isArray(value)) {
      return _.map(value, item => this.sanitizeValue(item, seen));
    }

    const sanitizedObject = {};
    _.forEach(_.keys(value), key => {
      sanitizedObject[key] = this.sanitizeValue(_.get(value, key), seen);
    });

    return sanitizedObject;
  }

  sanitizeMetricData(metricData) {
    return {
      componentName: _.get(metricData, "componentName", ""),
      location: _.get(metricData, "location", ""),
      orgId: _.get(metricData, "orgId", ""),
      props: this.sanitizeValue(_.get(metricData, "props", {})),
    };
  }

  /**
   * Registers a component usage in the analytics registry
   * @param {MetricData} metricData - The metric data to register
   * @returns {void}
   */
  registerComponent(metricData) {
    // Validate required fields
    if (
      !recordDesignSystemUsageMetrics ||
      _.isNil(metricData) ||
      _.isEmpty(_.trim(metricData.componentName))
    ) {
      return;
    }

    const sanitizedMetricData = this.sanitizeMetricData(metricData);

    if (this.worker) {
      this.worker.postMessage({
        type: "REGISTER",
        payload: sanitizedMetricData,
      });
    }
  }

  flushRegistry() {
    if (recordDesignSystemUsageMetrics && this.worker) {
      this.worker.postMessage({
        type: "FLUSH",
      });
    }
  }
}

// Export singleton instance
const dsComponentMetricsManager = new DSComponentMetricsManager();

export default dsComponentMetricsManager;
